import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PersonRequest } from '@app/models/project-setup.models';
import { PersonSimpleWithRole } from '@app/models/project.models';
import { PersonRoleType } from '@app/models/shared.enums';
import { resolveNameTitleCodeForSelect } from '@app/helpers/name-title-select.helper';
import { LocalizedValue, TitleNameData } from '@app/models/shared.models';
import {
  getNameFormGroup,
  getNameFormGroupFromPersonRequest,
  getNameFormGroupFromPersonSimpleWithRole,
  getNameFormValueFromPersonSimpleWithRole,
  NameFormData,
  NameFormGroup,
  patchNameFormFromPersonSimpleWithRole,
} from '../name-form/name-form.helper';

export interface TitleNameFormData {
  title: string | null;
  name: NameFormData;
}

export interface TitleNameFormGroup {
  title: FormControl<string | null>;
  name: FormGroup<NameFormGroup>;
}

function requiredValidator(required: boolean) {
  return required ? [Validators.required] : [];
}

export function getTitleNameFormGroup(
  fb: FormBuilder,
  required: boolean = false,
  personRoleCode: PersonRoleType | null = null
): FormGroup<TitleNameFormGroup> {
  return fb.nonNullable.group({
    title: fb.control<string | null>(null, requiredValidator(required)),
    name: getNameFormGroup(fb, required, personRoleCode),
  });
}

export function getTitleNameFormGroupFromPersonRequest(
  fb: FormBuilder,
  required: boolean = false,
  person: PersonRequest,
  connectionTypeCode: PersonRoleType,
  nameTitles?: readonly LocalizedValue[],
): FormGroup<TitleNameFormGroup> {
  const titleValue =
    nameTitles?.length && person.titleCode != null
      ? resolveNameTitleCodeForSelect(person.titleCode, nameTitles)
      : person.titleCode;
  return fb.nonNullable.group({
    title: fb.control<string | null>(
      titleValue,
      requiredValidator(required)
    ),
    name: getNameFormGroupFromPersonRequest(
      fb,
      required,
      person,
      connectionTypeCode
    ),
  });
}

export function getTitleNameFormGroupFromPersonSimpleWithRole(
  fb: FormBuilder,
  required: boolean = false,
  person: PersonSimpleWithRole,
  nameTitles?: readonly LocalizedValue[],
): FormGroup<TitleNameFormGroup> {
  const titleValue =
    nameTitles?.length && person.titleCode != null
      ? resolveNameTitleCodeForSelect(person.titleCode, nameTitles)
      : person.titleCode;
  return fb.nonNullable.group({
    title: fb.control<string | null>(
      titleValue,
      requiredValidator(required)
    ),
    name: getNameFormGroupFromPersonSimpleWithRole(fb, required, person),
  });
}

export function getTitleNameData(
  formGroup: FormGroup<TitleNameFormGroup>
): TitleNameData {
  return {
    title: formGroup.controls.title.value,
    connectionTypeCode:
      formGroup.controls.name.controls.connectionTypeCode.value,
    lastName: formGroup.controls.name.controls.lastName.value!,
    middleName: formGroup.controls.name.controls.middleName.value,
    firstName: formGroup.controls.name.controls.firstName.value!,
  };
}

export function patchTitleNameFormFromPersonSimpleWithRole(
  formGroup: FormGroup<TitleNameFormGroup>,
  person: PersonSimpleWithRole,
  nameTitles?: readonly LocalizedValue[],
): void {
  const raw = person.titleCode;
  formGroup.controls.title.setValue(
    nameTitles?.length
      ? resolveNameTitleCodeForSelect(raw, nameTitles)
      : raw,
  );
  patchNameFormFromPersonSimpleWithRole(formGroup.controls.name, person);
}

export function getTitleNameFormValueFromPersonSimpleWithRole(
  person: PersonSimpleWithRole | null,
  nameTitles?: readonly LocalizedValue[],
): TitleNameFormData {
  return {
    title:
      person === null
        ? null
        : nameTitles?.length
          ? resolveNameTitleCodeForSelect(person.titleCode, nameTitles)
          : person.titleCode,
    name: getNameFormValueFromPersonSimpleWithRole(
      person === null ? null : person
    ),
  };
}
